import { ReactElement, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Stack } from '@mui/material'
import { IfcPropertiesView } from '@editor/components/IFCElementForm/components'
import { CorneredSection } from '@ui2/structure'
import { useTranslucentGltfModel } from '@editorHooks'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import IFCImportToolsSection from '../IFCImportToolsSection'
import OpeningImportModeTabs from './OpeningImportModeTabs'

/**
 * Import step for wall openings the automatic wall import missed. The user selects the IFC opening
 * elements in the scene and imports them either via the "Automatisch" tab (the target wall of each
 * opening is inferred from its geometry, all openings are parsed with one request) or the
 * "Erweitert" tab (pick the target wall manually, reusing `IFCParseAsWallOpeningForm` from the free
 * "Element importieren" panel). Unlike the wall/roof import steps, nothing is pre-selected —
 * openings are picked manually.
 */
const IFCOpeningImportPanel = (): ReactElement => {
  const { projectId = '' } = useParams()
  const { activeGlbFile, metadata, selectedIfcElementGuids, setSelectedIfcElementGuids } =
    useIFCElementSelection()

  // Unlike the wall/roof import steps, openings are not pre-selected. Clear any selection carried
  // over from the preceding import step so this step starts empty and openings are picked manually.
  useEffect(() => {
    setSelectedIfcElementGuids([])
  }, [setSelectedIfcElementGuids])

  // Render the GLB translucent while importing so openings stay visible.
  useTranslucentGltfModel()

  return (
    <Stack spacing={2}>
      <CorneredSection title="Auswahl">
        <OpeningImportModeTabs
          guids={selectedIfcElementGuids}
          projectId={projectId}
          activeGlbFile={activeGlbFile}
          onSuccess={() => setSelectedIfcElementGuids([])}
          showSelectionInfo
        />
      </CorneredSection>

      {metadata && selectedIfcElementGuids.length > 0 && (
        <CorneredSection title="Eigenschaften" collapsible defaultOpen>
          <IfcPropertiesView
            guids={selectedIfcElementGuids}
            metadata={metadata}
            onSelectGuids={setSelectedIfcElementGuids}
          />
        </CorneredSection>
      )}

      <IFCImportToolsSection />
    </Stack>
  )
}

export default IFCOpeningImportPanel
